import React, { useState } from 'react';
import { GameState, ChallengeResult } from '../../types';
import DieFace from '../DieFace';

interface RevealPhaseOverlayProps {
  gameState: GameState;
  challengeResult: ChallengeResult | null;
  myPlayerId: string | null;
  onReadyUp: () => void;
}

const RevealPhaseOverlay: React.FC<RevealPhaseOverlayProps> = ({
  gameState,
  challengeResult,
  myPlayerId,
  onReadyUp,
}) => {
  const [hasReadied, setHasReadied] = useState(false);
  const [expandedThoughts, setExpandedThoughts] = useState<Set<string>>(new Set());

  const handleReadyUp = () => {
    setHasReadied(true);
    onReadyUp();
  };

  const toggleThoughts = (playerId: string) => {
    setExpandedThoughts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(playerId)) {
        newSet.delete(playerId);
      } else {
        newSet.add(playerId);
      }
      return newSet;
    });
  };

  const isReady = (playerId: string) => {
    return gameState.playersReady?.includes(playerId) || false;
  };

  const humanPlayers = gameState.players.filter(p => !p.isAI && p.dice.length > 0);
  const readyCount = humanPlayers.filter(p => isReady(p.id)).length;
  const totalHumans = humanPlayers.length;

  if (!challengeResult) return null;

  // Find the challenger and reorder players so challenger is at bottom
  const challengerPlayer = gameState.players.find(p => p.name === challengeResult.challenger);
  const challengerIndex = challengerPlayer ? gameState.players.indexOf(challengerPlayer) : -1;

  // Reorder: start from the player after challenger (in turn order), end with challenger
  const orderedPlayers = challengerIndex >= 0
    ? [
        ...gameState.players.slice(challengerIndex + 1),
        ...gameState.players.slice(0, challengerIndex + 1)
      ]
    : gameState.players;

  // Get each player's last bid
  const getPlayerLastBid = (playerId: string) => {
    // Search bidHistory in reverse to find last bid by this player
    for (let i = gameState.bidHistory.length - 1; i >= 0; i--) {
      if (gameState.bidHistory[i].playerId === playerId) {
        return gameState.bidHistory[i];
      }
    }
    return null;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Round Over!
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              {challengeResult.challenger} challenged {challengeResult.challenged}
            </p>
            <p className="text-xl font-semibold mt-2 text-primary-600 dark:text-primary-400">
              {challengeResult.loser} loses a die
            </p>
          </div>

          {/* Challenge Details */}
          <div className="bg-surface-50 dark:bg-gray-700 rounded-xl p-4 mb-6">
            <div className="flex flex-col items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-gray-700 dark:text-gray-300">Bid:</span>
                <span className="text-2xl font-bold text-gray-900 dark:text-white">
                  {challengeResult.bid.quantity}
                </span>
                <span className="text-lg text-gray-500 dark:text-gray-400">×</span>
                <DieFace value={challengeResult.bid.faceValue} size="sm" />
              </div>
              <div className="text-gray-700 dark:text-gray-300">
                Actual count: <span className="font-bold text-lg">{challengeResult.actualCount}</span>
              </div>
            </div>
          </div>

          {/* Revealed Dice */}
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 text-center">
              Everyone's Dice
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {orderedPlayers.map(player => {
                const dice = challengeResult.revealedDice[player.id] || [];
                const aiThought = player.isAI && challengeResult.aiThoughts[player.id] ? challengeResult.aiThoughts[player.id] : null;
                const isExpanded = expandedThoughts.has(player.id);
                const lastBid = getPlayerLastBid(player.id);

                return (
                  <div
                    key={player.id}
                    className="bg-surface-50 dark:bg-gray-700 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {player.emoji} {player.name}
                          {player.id === myPlayerId && ' (You)'}
                        </span>
                        {lastBid && (
                          <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
                            <span>({lastBid.quantity}×</span>
                            <DieFace value={lastBid.faceValue} size="xs" />
                            <span>)</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {aiThought && (
                          <button
                            onClick={() => toggleThoughts(player.id)}
                            className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium transition-colors"
                            aria-label={isExpanded ? "Hide AI thoughts" : "Show AI thoughts"}
                          >
                            {isExpanded ? '💭 Hide' : '💭 Show'}
                          </button>
                        )}
                        {isReady(player.id) && (
                          <span className="text-green-600 dark:text-green-400 text-sm font-medium">
                            ✓ Ready
                          </span>
                        )}
                      </div>
                    </div>

                    {aiThought && isExpanded && (
                      <div className="mb-3 p-3 bg-white dark:bg-gray-600 rounded-lg border-l-4 border-primary-500">
                        <p className="text-sm text-gray-700 dark:text-gray-200 italic">
                          {aiThought}
                        </p>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2">
                      {dice.map((die, idx) => (
                        <DieFace
                          key={idx}
                          value={die.value}
                          size="xs"
                          className={
                            die.value === challengeResult.bid.faceValue
                              ? 'ring-4 ring-primary-500 dark:ring-primary-400'
                              : ''
                          }
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Ready Status */}
          <div className="bg-surface-50 dark:bg-gray-700 rounded-xl p-4 mb-6">
            <div className="text-center">
              <p className="text-gray-700 dark:text-gray-300">
                Players Ready: <span className="font-bold">{readyCount}/{totalHumans}</span>
              </p>
            </div>
          </div>

          {/* Ready Up Button */}
          {myPlayerId && !isReady(myPlayerId) && !hasReadied && (
            <button
              onClick={handleReadyUp}
              className="w-full bg-primary-500 hover:bg-primary-600 text-white font-bold py-4 px-6 rounded-xl transition-colors shadow-lg hover:shadow-xl"
            >
              Ready for Next Round
            </button>
          )}

          {(isReady(myPlayerId || '') || hasReadied) && (
            <div className="text-center">
              <p className="text-green-600 dark:text-green-400 font-semibold">
                ✓ You're ready! Waiting for other players...
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RevealPhaseOverlay;
